<template>
  <login-overlay class="login v-login">
    <totp-login-form
      v-if="totpRequired"
      ref="totpLoginForm"
      :errors="totpErrors"
      :pending="isSigningIn"
      :rules="totpRules"
      :totp="totp"
      :username="totpUsername"
      @back="onBackToLogin"
      @clearErrors="totpErrors = {}"
      @submit="onTotpSubmit"
    />
    <login-form
      v-else
      ref="loginForm"
      :domain-name="DEFAULT_DOMAIN_NAME"
      :errors="errors"
      :pending="isSigningIn"
      :rules="rules"
      :session-id="'0'"
      :user="user"
      @clearErrors="errors = {}"
      @submit="onSubmit"
    />
  </login-overlay>
</template>
<script>
  import AuthorizationError from '@/common/utils/errors/AuthorizationError';
  import ValidationError from '@/common/utils/errors/ValidationError';
  import LoginOverlay from '@/common/components/user/LoginOverlay.vue';
  import LoginForm from '@/common/components/user/LoginForm.vue';
  import TotpLoginForm from '@/common/components/user/TotpLoginForm.vue';
  import { userNameRule } from '@/common/validation/nameRules';
  import { passwordRule, totpCodeRule } from '@/common/validation/authRules';
  import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
  import LoginPageMixin from '@/common/mixins/loginPage.mixin';
  import useAuthStore from '@/common/store/pinia/auth';
  import { mapState, mapStores } from 'pinia';
  import useAppStore from '@/common/store/pinia/app';
  import TotpAuthorizationError from '@/common/utils/errors/TotpAuthorizationError';

  export default {
    name: 'Login',
    components: {
      LoginOverlay,
      LoginForm,
      TotpLoginForm
    },
    mixins: [LoginPageMixin],
    data() {
      return {
        DEFAULT_DOMAIN_NAME,
        user: {
          username: '',
          password: ''
        },
        errors: {},
        totpRequired: false,
        totpReceipt: '',
        totpUsername: '',
        totp: {
          code: ''
        },
        totpErrors: {}
      };
    },
    created() {
      if (this.$route.query.failed) {
        this.errors = {
          username: false,
          password: false,
          backend: this.$t('Login has been disabled by the administrator')
        };
      }
    },
    computed: {
      ...mapStores(useAuthStore, useAppStore),
      ...mapState(useAuthStore, ['authReceiptExpired']),
      rules() {
        return {
          username: userNameRule,
          password: passwordRule()
        };
      },
      totpRules() {
        return {
          code: totpCodeRule()
        };
      }
    },
    watch: {
      authReceiptExpired: {
        handler(expired) {
          if (expired && this.totpRequired) {
            this.onBackToLogin();
            this.errors = {
              username: true,
              password: true,
              backend: this.$t('Two-factor authentication has timed out. Please log in again.')
            };
          }
        }
      }
    },
    methods: {
      onSubmit() {
        this.errors = {};
        this.$refs.loginForm.validate((valid) => {
          if (valid) {
            this.authStore.signIn({
              username: this.user.username.trim(),
              password: this.user.password
            })
              .catch(this.onError);
          }
        });
      },
      onTotpSubmit() {
        this.totpErrors = {};
        this.$refs.totpLoginForm.validate((valid) => {
          if (valid) {
            this.authStore.signInTotp(this.totpUsername, this.totp.code, this.totpReceipt)
              .catch(this.onTotpError);
          }
        });
      },
      onBackToLogin() {
        this.totpRequired = false;
        this.totpReceipt = '';
        this.totpUsername = '';
        this.totp.code = '';
        this.totpErrors = {};
        this.user.password = null;
      },
      onError(err) {
        this.sendPostMessage('authFailed');

        if (err instanceof TotpAuthorizationError) {
          this.totpReceipt = err.totpReceipt;
          this.totpUsername = this.user.username.trim();
          this.totpRequired = true;
          return;
        }

        if (err instanceof AuthorizationError) {
          this.errors = {
            username: true,
            password: true,
            backend: this.$t('Incorrect login or password')
          };
        } else {
          this.$router.push({ name: 'errorServiceDisabled' });
        }
      },
      onTotpError(err) {
        this.sendPostMessage('authFailed');
        if (err instanceof ValidationError || err instanceof AuthorizationError) {
          this.totpErrors = {
            code: true,
            backend: this.$t('The entered code is incorrect or outdated')
          };
        } else {
          this.$router.push({ name: 'errorServiceDisabled' });
        }
      },
      getLandingPage() {
        return this.appStore.fetchHciLandingPage();
      }
    }
  };
</script>
